// Documents for the section demos, built with the Builder like the hero.
import { PALETTE as C } from './hero.js';

const r3 = (v) => Math.round(v * 1000) / 1000;

/** The PulseScript demo: a core to drag, a counter, a floor. The script (src/scripts/orbs.ts) does the rest. */
export function orbsDoc(Pulse, script) {
  const W = 960, H = 540;
  const d = Pulse.doc({ width: W, height: H, duration: 8, fps: 60, background: '#0a0716', title: 'Orb launcher' }).beats(120, { until: 8 });
  d.json.root.loop = true;
  d.solid({ radial: { center: { x: W / 2, y: H * 0.45 }, radius: 640, stops: [{ at: 0, color: '#1d0f3d' }, { at: 1, color: '#0a0716' }] } });
  for (let i = 1; i < 12; i++) d.line([[i * W / 12, 0], [i * W / 12, H - 36]], { stroke: '#ffffff0d', width: 1 });
  d.line([[0, H - 36], [W, H - 36]], { stroke: C.violet, width: 2 });
  d.defineSymbol('core', { duration: 2, width: 96, height: 96, loop: true }, (tl) => {
    tl.circle(48, 48, 34, { fill: { radial: { center: { x: 40, y: 40 }, radius: 40, stops: [{ at: 0, color: '#ffffff' }, { at: 0.4, color: C.cyan }, { at: 1, color: C.violet }] } } });
    tl.circle(48, 48, 44, { fill: null, stroke: C.magenta, strokeWidth: 3, cap: 'round', trim: { start: 0, end: 0.35, offset: Pulse.keys([0, 0], [2, 1]) } });
  });
  d.symbol('core', { id: 'core', x: W / 2, y: H * 0.55 }).effect('glow', { radius: 18, intensity: 1, color: C.cyan });
  d.text('fired 0  ·  live 0', { id: 'count', size: 20, weight: 700, fill: '#ffffffcc', align: 'left', x: 24, y: 32, anchor: { x: 0, y: 0.5 } });
  d.text('click to fire · drag the core · Space for a ring', { id: 'hint', size: 16, fill: '#ffffff80', x: W / 2, y: H - 14 });
  const doc = d.build();
  doc.scripts = [script];
  return doc;
}

// Keys that swing between -a and +a from phase p, every T seconds for D seconds.
function swing(Pulse, a, T, D, p = 0, base = 0) {
  const k = [];
  for (let t = 0, i = 0; t <= D + 1e-9; t += T / 2, i++) k.push([r3(t), base + ((i % 2) ? -a : a) * (p ? -1 : 1), 'in_out_sine']);
  return Pulse.keys(...k);
}

/**
 * The bones demo: a rigid figure (limb symbols bound to a skeleton, the right hand waving by a
 * keyed IK target, which the puppet script overrides with the pointer) and a tube shape skinned
 * to four bones with automatic weights. Adapted from pulse-player's examples/bones.
 */
export function bonesDoc(Pulse, script) {
  const W = 1280, H = 720, D = 4, ox = 120;
  const d = Pulse.doc({ width: W, height: H, duration: D, fps: 30, background: '#0a0716', title: 'Bones' });
  d.json.root.loop = true;
  d.rect(0, 580, W, 140, { fill: { linear: { from: { x: 0, y: 580 }, to: { x: 0, y: 720 }, stops: [{ at: 0, color: '#1d1238' }, { at: 1, color: '#0a0716' }] } } });
  d.defineSymbol('limb', { width: 100, height: 30, duration: 1 }, (tl) => tl.rect(0, 0, 100, 30, { radius: 15, fill: C.magenta, stroke: '#ffffff', strokeWidth: 3 }));
  d.defineSymbol('torso', { width: 100, height: 70, duration: 1 }, (tl) => tl.rect(0, 0, 100, 70, { radius: 26, fill: C.violet, stroke: '#ffffff', strokeWidth: 3 }));
  d.defineSymbol('head', { width: 80, height: 80, duration: 1 }, (tl) => { tl.circle(40, 40, 38, { fill: C.cyan, stroke: '#ffffff', strokeWidth: 3 }); tl.circle(52, 34, 5, { fill: '#0a0716' }); tl.rect(40, 52, 22, 6, { radius: 3, fill: '#0a0716' }); });

  const B = {
    spine: { x: ox + 320, y: 490, length: 170, angle: -90 },
    head: { parent: 'spine', x: ox + 320, y: 320, length: 76, angle: -90, limits: { min: -25, max: 25 } },
    r_up: { parent: 'spine', x: ox + 350, y: 350, length: 110, angle: -20 },
    r_fore: { parent: 'r_up', length: 100, angle: -70, limits: { min: -150, max: 10 } },
    r_hand: { parent: 'r_fore', length: 36, angle: -70, ik: { chain: 3 } },
    l_up: { parent: 'spine', x: ox + 290, y: 350, length: 110, angle: 110 },
    l_fore: { parent: 'l_up', length: 100, angle: 100, limits: { min: -60, max: 60 } },
    leg_l: { x: ox + 305, y: 490, length: 130, angle: 100 },
    leg_r: { x: ox + 335, y: 490, length: 130, angle: 80 },
  };
  const arm = d.armature('figure');
  for (const [id, o] of Object.entries(B)) { arm.bone(id, o); Object.assign(o, arm.json.bones.find((b) => b.id === id)); }
  const L = d.layer('figure');
  const onBone = (sym, id, b, thick = 1) => L.symbol(sym, { id, x: b.x, y: b.y, rotation: b.angle, scale_x: b.length / 100, scale_y: thick, anchor: { x: 0, y: 0.5 } });
  onBone('limb', 'legL', B.leg_l, 1.2); onBone('limb', 'legR', B.leg_r, 1.2); onBone('limb', 'armL1', B.l_up); onBone('limb', 'armL2', B.l_fore, 0.9);
  L.symbol('torso', { id: 'torso', x: B.spine.x, y: B.spine.y, rotation: -90, scale_x: 1.9, scale_y: 1.3, anchor: { x: 0, y: 0.5 } });
  onBone('head', 'head', B.head, 1); onBone('limb', 'armR1', B.r_up); onBone('limb', 'armR2', B.r_fore, 0.9); onBone('limb', 'handR', B.r_hand, 1.2);
  for (const [item, bone] of [['legL', 'leg_l'], ['legR', 'leg_r'], ['armL1', 'l_up'], ['armL2', 'l_fore'], ['torso', 'spine'], ['head', 'head'], ['armR1', 'r_up'], ['armR2', 'r_fore'], ['handR', 'r_hand']]) arm.bind(item, bone);
  const wave = [];
  for (let t = 0, i = 0; t <= D + 1e-9; t += 0.5, i++) wave.push([t, { x: ox + 470 + (i % 2 ? -60 : 40), y: 170 + (i % 2 ? 10 : 0) }, 'in_out_sine']);
  arm.target('r_hand', Pulse.keys(...wave));
  arm.rotate('l_up', swing(Pulse, 18, 2, D)).rotate('l_fore', swing(Pulse, 30, 2, D, 1)).rotate('head', swing(Pulse, 10, 1, D)).rotate('spine', swing(Pulse, 4, 2, D, 1));

  // The skinned tube, on the right.
  const x0 = 700, y0 = 380, len = 480, n = 16, r = 34, top = [], bot = [];
  for (let i = 0; i <= n; i++) { const x = x0 + len * i / n, w = r * (1 - 0.6 * i / n); top.push(x, y0 - w); bot.unshift(x, y0 + w); }
  d.layer('tube').shape([{ closed: true, points: [...top, ...bot], smooth: 0.16 }], { id: 'tube', fill: { linear: { from: { x: x0, y: 0 }, to: { x: x0 + len, y: 0 }, stops: [{ at: 0, color: C.volt }, { at: 1, color: C.cyan }] } }, stroke: '#ffffff', strokeWidth: 3 });
  const tube = d.armature('tube-rig');
  let parent = null;
  for (let i = 0; i < 4; i++) { tube.bone('t' + i, { parent, x: parent ? undefined : x0, y: parent ? undefined : y0, length: len / 4, angle: 0 }); parent = 't' + i; }
  tube.skin('tube', { influences: 2, smooth: 2 });
  for (let i = 0; i < 4; i++) tube.rotate('t' + i, swing(Pulse, 14 + i * 4, 2, D));
  Object.values(tube.json.pose).forEach((p, i) => { p.rotation.keys = p.rotation.keys.map((k) => ({ ...k, t: r3(k.t + i * 0.25) })); });

  d.text('move the pointer over the figure: its hand follows by IK', { size: 22, fill: '#ffffff99', x: W / 2, y: 660 });
  const doc = d.build();
  doc.scripts = [script];
  return doc;
}
